import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { dynamicAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../utils/seo';
import Breadcrumbs from '../components/Breadcrumbs';
import RichContent from '../components/RichContent';
import EventCard from '../components/EventCard';
import LoadingIndicator from '../components/LoadingIndicator';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import './AttractionDetail.css';


const FALLBACK_IMG = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600';

const HIGHLIGHT_FALLBACK_ICONS = ['landscape', 'schedule', 'temple_hindu'];
const THING_FALLBACK_ICONS = ['temple_hindu', 'groups', 'restaurant_menu'];

/**
 * Build highlight cards from tags when admin JSON is empty.
 * Supports multi-line tags: a line without ":" is merged into the previous value
 * (e.g. "Altitude: 6" + "400 ft" → "6,400 ft" when value ends with a digit and continuation starts with a digit).
 */
function highlightsFromTags(tags) {
  if (!Array.isArray(tags)) return [];
  const lines = [];
  for (const t of tags) {
    for (const part of String(t).split(/\r?\n/)) {
      const s = part.trim();
      if (s) lines.push(s);
    }
  }
  const merged = [];
  for (const line of lines) {
    const idx = line.indexOf(':');
    if (idx === -1) {
      if (merged.length > 0) {
        const prev = merged[merged.length - 1];
        const tail = line.trim();
        const pv = prev.value.trim();
        if (/\d$/.test(pv) && /^\d/.test(tail)) {
          prev.value = `${pv},${tail}`;
        } else {
          prev.value = `${pv} ${tail}`.replace(/\s+/g, ' ').trim();
        }
      }
      continue;
    }
    const label = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (label) merged.push({ label, value });
  }
  return merged
    .filter((h) => h.label && h.value)
    .slice(0, 6)
    .map((h, i) => ({
      ...h,
      icon: HIGHLIGHT_FALLBACK_ICONS[i % HIGHLIGHT_FALLBACK_ICONS.length]
    }));
}

/** `loadSimilarTreksForPublic` / attraction API row → EventCard `event` prop */
function similarTrekRowToEventCardEvent(t) {
  if (!t || typeof t !== 'object') return {};
  const trekId = t.id;
  const slug = t.slug != null && String(t.slug).trim() !== '' ? String(t.slug).trim() : String(trekId ?? '');
  return {
    id: trekId,
    batchId: t.batchId ?? undefined,
    trekId,
    slug,
    title: t.name || 'Trek',
    imageUrl: t.imageUrl,
    media: Array.isArray(t.media) && t.media.length ? t.media : undefined,
    shortDescription: String(t.tagline || t.description || '').trim(),
    duration: String(t.duration || '').trim() || 'TBA',
    startDate: t.startDate,
    departureDates: Array.isArray(t.departureDates) ? t.departureDates : t.startDate ? [t.startDate] : undefined,
    location: t.location || '',
    difficulty: t.difficulty || 'Moderate',
    price: t.price,
    brochureUrl: t.brochureUrl || null
  };
}

const AttractionDetail = () => {
  const { settings } = useSiteSettings();
  const brandName = String(settings?.site_brand_name || 'INVINCIBLE').trim() || 'INVINCIBLE';
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const id = slugParam ? decodeURIComponent(slugParam) : '';
    if (!id) {
      setLoading(false);
      setError('not_found');
      return undefined;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await dynamicAPI.getAttractionPublic(id);
        if (cancelled) return;
        const d = res?.data?.data;
        if (!d) {
          setError('not_found');
          setData(null);
        } else {
          setData(d);
          const name = d.name || 'Place';
          document.title = `${name} - Attraction Details | ${brandName}`;
        }
      } catch {
        if (!cancelled) {
          setError('not_found');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slugParam, brandName]);

  const publicBase =
    String(settings?.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  const attractionSchema = useMemo(() => {
    if (!data) return null;
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Events & Attractions', url: '/events' },
      { name: data.name || 'Attraction', url: `/attractions/${slugParam}` }
    ], publicBase);
    const place = {
      '@context': 'https://schema.org',
      '@type': 'TouristAttraction',
      name: data.name,
      description: data.brief || data.description ? String(data.brief || data.description).slice(0, 200) : undefined,
      image: data.imageUrl || undefined,
      url: `${publicBase}/attractions/${slugParam}`
    };
    return {
      '@context': 'https://schema.org',
      '@graph': [place, breadcrumbs]
    };
  }, [data, publicBase, slugParam]);

  usePageSeo({
    enabled: Boolean(data),
    title: data?.name ? `${data.name} | ${brandName}` : `Attraction | ${brandName}`,
    description: (data?.brief || data?.description ? String(data.brief || data.description).slice(0, 160) : '').trim() || settings?.site_meta_description,
    canonical: slugParam && publicBase ? `${publicBase}/attractions/${slugParam}` : undefined,
    image: data?.imageUrl || undefined,
    schema: attractionSchema
  });

  const backHref = typeof from === 'string' && from.startsWith('/') ? from : '/events';
  const backOnClick = () => {
    if (typeof from === 'string' && from.startsWith('/')) navigate(from);
    else navigate(-1);
  };

  if (loading) {
    return (
      <div className="attraction-detail-page">
        <div className="ad-loading">
          <LoadingIndicator label="Loading..." />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="attraction-detail-page">
        <div className="ad-error">
          <h1>Place not found</h1>
          <p>This attraction may have been removed or the link is incorrect.</p>
          <Link to="/events" className="ad-btn-events">
            Browse events
          </Link>
        </div>
      </div>
    );
  }

  const heroSrc = optimizeImageUrl(data.imageUrl || FALLBACK_IMG, {
    width: IMAGE_DISPLAY_WIDTH.hero
  });
  const gallery = Array.isArray(data.galleryUrls)
    ? data.galleryUrls
        .filter(Boolean)
        .map((u) => optimizeImageUrl(u, { width: IMAGE_DISPLAY_WIDTH.gallery }))
    : [];
  const faqs = Array.isArray(data.faqs) ? data.faqs.filter((f) => f && (f.q || f.a)) : [];
  const leftFaqs = faqs.filter((_, i) => i % 2 === 0);
  const rightFaqs = faqs.filter((_, i) => i % 2 === 1);

  const highlightsRaw = Array.isArray(data.highlights) ? data.highlights.filter((h) => h?.label && h?.value) : [];
  const highlights =
    highlightsRaw.length > 0 ? highlightsRaw : highlightsFromTags(data.tags);

  const thingsToDo = Array.isArray(data.thingsToDo)
    ? data.thingsToDo.filter((t) => t?.title && (t.description || t.body))
    : [];

  const treksFeaturingAttraction = Array.isArray(data.treksFeaturingAttraction)
    ? data.treksFeaturingAttraction
    : [];
  const featuringIds = new Set(treksFeaturingAttraction.map((t) => t.id).filter(Boolean));
  const similarTreksRaw = Array.isArray(data.similarTreks) ? data.similarTreks : [];
  const similarTreks = similarTreksRaw.filter((t) => t?.id != null && !featuringIds.has(t.id));

  const tagline = (data.subtitle || data.brief || '').trim();
  const aboutTitle = (data.aboutTitle || '').trim() || `About ${String(data.name || 'Attraction').trim()}`;
  const thingsToDoTitle = (data.thingsToDoTitle || '').trim() || 'Things To Do';
  const galleryTitle = (data.galleryTitle || '').trim() || 'Photo Gallery';
  const nearbyTitle = (data.nearbyTitle || '').trim() || 'Nearby Treks';
  const featuringTitle =
    (data.treksFeaturingTitle || '').trim() || 'Treks that visit this place';

  return (
    <div className="attraction-detail-page">
      <div className="ad-back-row">
        <Breadcrumbs
          items={[
            { label: 'Events', path: '/events' },
            { label: data.name || 'Attraction' }
          ]}
          className="ad-breadcrumbs"
        />
        {from ? (
          <button type="button" className="ad-back" onClick={backOnClick}>
            <ArrowLeft size={16} aria-hidden />
            Back to event
          </button>
        ) : (
          <Link to={backHref} className="ad-back">
            <ArrowLeft size={16} aria-hidden />
            Back
          </Link>
        )}
      </div>
      <div className="ad-hero-outer">
        <div className="ad-hero">
          <div className="ad-hero-bg" style={{ backgroundImage: `url("${heroSrc.replace(/"/g, '%22')}")` }} />
          <div className="ad-hero-gradient" aria-hidden />
        </div>
      </div>

      <section className="ad-section">
        <div className="ad-container">
          <div className="ad-title-block">
            <h1 className="ad-main-title">{data.name}</h1>
            {tagline ? <p className="ad-tagline">{tagline}</p> : null}
          </div>

          {(data.description || data.brief) && (
            <div className="ad-about-card">
              <h3 className="ad-about-heading">{aboutTitle}</h3>
              <div className="ad-about-body">
                {data.description ? (
                  <RichContent content={data.description} className="ad-rich" />
                ) : (
                  <p className="ad-about-fallback">{data.brief}</p>
                )}
              </div>
            </div>
          )}

          {highlights.length > 0 && (
            <div className="ad-highlights-grid">
              {highlights.map((h, i) => (
                <div key={`${h.label}-${i}`} className="ad-highlight-card">
                  <div className="ad-highlight-icon-wrap">
                    <span className="material-symbols-outlined ad-ms">{h.icon || HIGHLIGHT_FALLBACK_ICONS[i % 3]}</span>
                  </div>
                  <div>
                    <h4 className="ad-highlight-label">{h.label}</h4>
                    <p className="ad-highlight-value">{h.value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {treksFeaturingAttraction.length > 0 && (
            <div className="ad-block">
              <h3 className="ad-section-heading ad-section-heading--flat">{featuringTitle}</h3>
              <div className="ad-event-cards-similar">
                <div className="ad-event-cards-similar-inner">
                  {treksFeaturingAttraction.map((t) => (
                    <EventCard key={t.id} event={similarTrekRowToEventCardEvent(t)} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {thingsToDo.length > 0 && (
            <div className="ad-block">
              <h3 className="ad-section-heading">{thingsToDoTitle}</h3>
              <div className="ad-things-grid">
                {thingsToDo.map((t, i) => {
                  const icon = t.icon || THING_FALLBACK_ICONS[i % THING_FALLBACK_ICONS.length];
                  const desc = t.description || t.body || '';
                  const inner = (
                    <>
                      <div className="ad-thing-top">
                        <div className="ad-thing-icon-wrap">
                          <span className="material-symbols-outlined ad-ms">{icon}</span>
                        </div>
                        <span className="material-symbols-outlined ad-thing-arrow ad-ms">arrow_outward</span>
                      </div>
                      <h4 className="ad-thing-title">{t.title}</h4>
                      <p className="ad-thing-desc">{desc}</p>
                    </>
                  );
                  if (t.url) {
                    const ext = /^https?:\/\//i.test(t.url);
                    if (ext) {
                      return (
                        <a
                          key={`${t.title}-${i}`}
                          href={t.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ad-thing-card"
                        >
                          {inner}
                        </a>
                      );
                    }
                    return (
                      <Link key={`${t.title}-${i}`} to={t.url} className="ad-thing-card">
                        {inner}
                      </Link>
                    );
                  }
                  return (
                    <div key={`${t.title}-${i}`} className="ad-thing-card">
                      {inner}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {gallery.length > 0 && (
            <div className="ad-block">
              <h3 className="ad-section-heading">{galleryTitle}</h3>
              <div className="ad-gallery-grid">
                {gallery.map((url, i) => (
                  <div key={`${url}-${i}`} className="ad-gallery-cell">
                    <img src={url} alt="" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {similarTreks.length > 0 && (
            <div className="ad-block">
              <h3 className="ad-section-heading ad-section-heading--flat">{nearbyTitle}</h3>
              <div className="ad-event-cards-similar">
                <div className="ad-event-cards-similar-inner">
                  {similarTreks.map((t) => (
                    <EventCard key={t.id} event={similarTrekRowToEventCardEvent(t)} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {faqs.length > 0 && (
            <div className="ad-about-card ad-faq-wrap">
              <h3 className="ad-section-heading ad-section-heading--in-card">{faqTitle}</h3>
              <div className="ad-faq-grid">
                <div className="ad-faq-column">
                  {leftFaqs.map((f, i) => (
                    <details key={`l-${i}`} className="ad-faq">
                      <summary>
                        <span>{f.q}</span>
                        <ChevronDown size={22} className="ad-faq-chevron" aria-hidden />
                      </summary>
                      <div className="ad-faq-body">{f.a}</div>
                    </details>
                  ))}
                </div>
                <div className="ad-faq-column">
                  {rightFaqs.map((f, i) => (
                    <details key={`r-${i}`} className="ad-faq">
                      <summary>
                        <span>{f.q}</span>
                        <ChevronDown size={22} className="ad-faq-chevron" aria-hidden />
                      </summary>
                      <div className="ad-faq-body">{f.a}</div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AttractionDetail;
